// ============================================================================
//  Lectura del resumen economico de un Playbook
// ----------------------------------------------------------------------------
//  El sumatorio lo calcula el CORE (`playbook_economics.py`, la misma funcion
//  que alimenta la Tienda de la app). Aqui NO se vuelve a sumar nada: solo se
//  aplana para pintarlo.
//
//  La regla dura del backend es que NUNCA se suman unidades distintas, y por eso
//  llegan tres cubos separados (pago unico / mensual / por uso) y cada uno como
//  LISTA, con una entrada por divisa (y por unidad, en el caso de "por uso").
//  Mezclarlos aqui reintroduciria justo el error que el backend evita.
// ============================================================================

/** Suma los cubos de UNA divisa. Devuelve null si no hay nada con precio. */
const bucketTotal = (buckets, currency) => {
   if (!Array.isArray(buckets) || !buckets.length) return null;
   const rows = buckets.filter((b) => b && b.count && (!currency || b.currency === currency));
   if (!rows.length) return null;
   return {
      min: rows.reduce((acc, b) => acc + Number(b.min || 0), 0),
      max: rows.reduce((acc, b) => acc + Number(b.max || 0), 0),
      currency: rows[0].currency,
      label: rows.length === 1 ? rows[0].label : '',
   };
};

/**
 * Totales de un playbook listos para pintar.
 *
 * `monthly` y `oneOff` se dan en la divisa principal que declara el backend; si
 * el playbook mezcla divisas (`mixed_currency`) se avisa en vez de sumar peras
 * con manzanas.
 */
export const playbookTotals = (economics) => {
   if (!economics) return null;
   const currency = economics.currency || 'EUR';
   return {
      currency,
      mixedCurrency: Boolean(economics.mixed_currency),
      // `is_partial`: hay procesos sin precio, asi que el total es un SUELO,
      // no el precio final. La interfaz esta obligada a decirlo.
      isPartial: Boolean(economics.is_partial),
      isEstimate: Boolean(economics.is_estimate),
      total: economics.integrations_total || 0,
      priced: economics.integrations_priced || 0,
      monthly: bucketTotal(economics.monthly, currency),
      oneOff: bucketTotal(economics.one_off, currency),
      // Por uso no se suma a nada: cada unidad es su propia magnitud.
      perUse: Array.isArray(economics.per_use) ? economics.per_use.filter((b) => b && b.count) : [],
      unpriced: Array.isArray(economics.unpriced) ? economics.unpriced : [],
   };
};

export const formatMoney = (amount, currency = 'EUR', locale = 'es-ES', decimals) => {
   const n = Number(amount);
   if (!Number.isFinite(n)) return '';
   // Los importes por uso llevan centimos (1,50 EUR/lead): redondearlos a 0
   // decimales convertiria el precio en otro. Los mensuales suelen ser enteros.
   const d = decimals === undefined ? (Number.isInteger(n) ? 0 : 2) : decimals;
   try {
      return new Intl.NumberFormat(locale, {
         style: 'currency',
         currency,
         minimumFractionDigits: d,
         maximumFractionDigits: d,
      }).format(n);
   } catch (e) {
      return `${n.toFixed(d)} ${currency}`;
   }
};

/** "400 - 600 EUR" o "400 EUR" si min y max coinciden. */
export const formatRange = (min, max, currency, locale) => {
   if (min === null || min === undefined) return '';
   // Los dos extremos se formatean con los MISMOS decimales: "1,50 - 3 EUR"
   // se lee como una errata.
   const decimals =
      Number.isInteger(Number(min)) && Number.isInteger(Number(max === undefined ? min : max))
         ? 0
         : 2;
   const lo = formatMoney(min, currency, locale, decimals);
   if (max === null || max === undefined || Number(max) === Number(min)) return lo;
   return `${lo} - ${formatMoney(max, currency, locale, decimals)}`;
};
